import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { combineReducers, configureStore } from '@reduxjs/toolkit';

import { mainThemeSlice } from '@sternum/web/components/theme/slice/MainTheme.slice';
import {
  alertsMiddleware,
  alertsRulesMiddleware,
  alertsRulesSlice,
  alertsSlice,
} from '@sternum/web/features/alerts/slice';
import { anomaliesMiddleware, anomaliesSlice } from '@sternum/web/features/anomalies/slice';
import {
  appContainerWithMenuMiddleware,
  appContainerWithMenuSlice,
} from '@sternum/web/features/appContainerWithMenu/slice';
import { auditLogMiddleware, auditLogSlice } from '@sternum/web/features/auditLog/slice';
import { dashboardMiddleware, dashboardSlice } from '@sternum/web/features/dashboard/slice';
import {
  deviceDetailsMiddleware,
  deviceDetailsSlice,
  deviceMiddleware,
  deviceSlice,
} from '@sternum/web/features/device/slice';
import { fleetsViewMiddleware, fleetsViewSlice } from '@sternum/web/features/fleetsView/slice';
import { logsMiddleware, logsSlice } from '@sternum/web/features/logs/slice';
import { userSlice } from '@sternum/web/features/user/slice';
import { workloadsSlice } from '@sternum/web/features/workloads/slice';

import { attacksMiddleware, attacksSlice } from '../features/attacks/slice';

import { clearReduxStoreAction } from './globalActions';

const rootReducer = combineReducers({
  mainThemeMode: mainThemeSlice.reducer,
  appContainerWithMenu: appContainerWithMenuSlice.reducer,
  user: userSlice.reducer,
  dashboard: dashboardSlice.reducer,
  device: deviceSlice.reducer,
  deviceDetails: deviceDetailsSlice.reducer,
  fleetsView: fleetsViewSlice.reducer,
  logs: logsSlice.reducer,
  anomalies: anomaliesSlice.reducer,
  alerts: alertsSlice.reducer,
  attacks: attacksSlice.reducer,
  alertsRules: alertsRulesSlice.reducer,
  workloads: workloadsSlice.reducer,
  auditLog: auditLogSlice.reducer,
});

const reducerFunction: typeof rootReducer = (state, action) => {
  if (action.type === clearReduxStoreAction.type) {
    // Make sure that this action will clear everything in every slice
    state = undefined;
  }

  return rootReducer(state, action);
};

export const store = configureStore({
  reducer: reducerFunction,
  middleware: (getDefaultMiddleware) => {
    return getDefaultMiddleware().concat(
      appContainerWithMenuMiddleware,
      dashboardMiddleware,
      deviceMiddleware,
      deviceDetailsMiddleware,
      fleetsViewMiddleware,
      logsMiddleware,
      alertsMiddleware,
      attacksMiddleware,
      alertsRulesMiddleware,
      anomaliesMiddleware,
      auditLogMiddleware,
    );
  },
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
